import React, { useState, useEffect, useContext } from 'react';
import api from '../services/api';
import { Link, useLocation } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { getBasePath } from '../utils/navigationHelper';

const QuizzesPage = () => {
  const { user } = useContext(AuthContext);
  const location = useLocation();
  const [quizzes, setQuizzes] = useState([]);
  const [page, setPage] = useState(1);
  const limit = 12; // Show more items per page for card layout
  const [total, setTotal] = useState(0);
  const [attemptCounts, setAttemptCounts] = useState({});
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCourses();
  }, []);

  useEffect(() => { 
    fetchQuizzes(); 
  }, [page, selectedCourse]);

  // Reset to page 1 when course filter changes
  useEffect(() => {
    if (page !== 1) {
      setPage(1);
    }
  }, [selectedCourse]);

  const fetchCourses = async () => {
    try {
      const res = await api.get('/courses', { params: { limit: 100 } });
      setCourses(res.data.data || res.data);
    } catch (err) {
      console.error('Failed to fetch courses:', err);
    }
  };

  const fetchQuizzes = async () => {
    setLoading(true);
    try {
      const params = { page, limit };
      if (selectedCourse) params.courseId = selectedCourse;
      
      const res = await api.get('/quizzes', { params });
      const { data, meta } = res.data;
      // Backend already filters by studentType and excludes lesson-attached quizzes
      setQuizzes(data);
      setTotal(meta.total);
      
      // Fetch attempt counts for each quiz
      const attemptData = {};
      for (const quiz of data) {
        try {
          const attemptRes = await api.get(`/submissions/quiz/${quiz.id}/attempts`);
          attemptData[quiz.id] = attemptRes.data;
        } catch (err) {
          console.error(`Failed to fetch attempts for quiz ${quiz.id}:`, err);
          attemptData[quiz.id] = { canAttempt: true, currentAttempts: 0, maxAttempts: null };
        }
      }
      setAttemptCounts(attemptData);
    } catch (error) {
      console.error('Error fetching quizzes:', error);
    } finally {
      setLoading(false);
    }
  };

  const totalPages = Math.ceil(total / limit);

  const isQuizBlocked = (quiz) => {
    const now = new Date();
    const openTime = quiz.openTime ? new Date(quiz.openTime) : null;
    const closeTime = quiz.closeTime ? new Date(quiz.closeTime) : null;
    
    // Check time-based blocking
    const timeBlocked = (openTime && now < openTime) || (closeTime && now > closeTime);
    
    // Check attempt-based blocking
    const attemptInfo = attemptCounts[quiz.id];
    const attemptBlocked = attemptInfo && !attemptInfo.canAttempt;

    return timeBlocked || attemptBlocked;
  };

  const getQuizStatus = (quiz) => {
    const now = new Date();
    const openTime = quiz.openTime ? new Date(quiz.openTime) : null;
    const closeTime = quiz.closeTime ? new Date(quiz.closeTime) : null;
    const attemptInfo = attemptCounts[quiz.id];

    if (openTime && now < openTime) {
      return { status: 'upcoming', label: 'Opens Soon', color: 'yellow' };
    } else if (closeTime && now > closeTime) {
      return { status: 'closed', label: 'Time Closed', color: 'red' };
    } else if (attemptInfo && !attemptInfo.canAttempt) {
      return { status: 'maxed', label: 'Max Attempts', color: 'red' };
    } else {
      return { status: 'available', label: 'Available', color: 'green' };
    }
  };

  const getTimeRemaining = (quiz) => {
    const now = new Date();
    const openTime = quiz.openTime ? new Date(quiz.openTime) : null;
    const closeTime = quiz.closeTime ? new Date(quiz.closeTime) : null;

    if (openTime && now < openTime) {
      const diff = openTime - now;
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      return `Opens in ${days}d ${hours}h`;
    } else if (closeTime && now < closeTime) {
      const diff = closeTime - now;
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      if (days > 0) return `${days}d ${hours}h remaining`;
      return `${hours}h remaining`;
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                  <svg className="w-8 h-8 text-blue-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Available Quizzes
                </h1>
                <p className="text-gray-600 mt-1">Take your quizzes and track your progress</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filter Section */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-8">
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
            <div className="flex items-center space-x-3">
              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.414A1 1 0 013 6.707V4z" />
              </svg>
              <label htmlFor="course-filter" className="text-sm font-medium text-gray-700">Filter by Course:</label>
            </div>
            <div className="flex flex-wrap gap-3 items-center">
              <select
                id="course-filter"
                value={selectedCourse}
                onChange={(e) => setSelectedCourse(e.target.value)}
                className="border border-gray-300 rounded-lg px-4 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              >
                <option value="">All Courses</option>
                {courses.map(course => (
                  <option key={course.id} value={course.id}>
                    {course.title}
                  </option>
                ))}
              </select>
              {selectedCourse && (
                <button
                  onClick={() => setSelectedCourse('')}
                  className="text-sm text-blue-600 hover:text-blue-800 font-medium bg-blue-50 hover:bg-blue-100 px-3 py-1 rounded-lg transition-colors duration-200"
                >
                  Clear Filter
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Loading State */}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="text-gray-600 mt-4">Loading quizzes...</p>
          </div>
        ) : (
          <>
            {/* Quizzes Grid */}
            {quizzes.length === 0 ? (
              <div className="text-center py-16">
                <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No quizzes found</h3>
                <p className="text-gray-500">
                  {selectedCourse ? 'No quizzes available for the selected course.' : 'No quizzes are currently available for your student type.'}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                {quizzes.map(quiz => {
                  const status = getQuizStatus(quiz);
                  const attemptInfo = attemptCounts[quiz.id];
                  const timeRemaining = getTimeRemaining(quiz);
                  const isBlocked = isQuizBlocked(quiz);

                  return (
                    <div key={quiz.id} className="bg-white rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-200">
                      {/* Status Badge */}
                      <div className="p-6">
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex-1">
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">{quiz.title}</h3>
                            {quiz.course && (
                              <div className="flex items-center text-sm text-gray-500 mb-2">
                                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C20.168 18.477 18.582 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                                </svg>
                                {quiz.course.title}
                              </div>
                            )}
                          </div>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            status.color === 'green' ? 'bg-green-100 text-green-800' :
                            status.color === 'yellow' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {status.label}
                          </span>
                        </div>

                        {/* Quiz Info */}
                        <div className="space-y-3 mb-6">
                          {/* Timing Info */}
                          <div className="flex items-center text-sm text-gray-600">
                            <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            {timeRemaining || 'No time limit'}
                          </div>

                          {/* Open/Close Times */}
                          {(quiz.openTime || quiz.closeTime) && (
                            <div className="text-xs text-gray-500 space-y-1">
                              {quiz.openTime && (
                                <div>Opens: {new Date(quiz.openTime).toLocaleDateString()} at {new Date(quiz.openTime).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</div>
                              )}
                              {quiz.closeTime && (
                                <div>Closes: {new Date(quiz.closeTime).toLocaleDateString()} at {new Date(quiz.closeTime).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</div>
                              )}
                            </div>
                          )}

                          {/* Attempt Info */}
                          {attemptInfo && (
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-gray-600">Attempts:</span>
                              <span className={`font-medium ${attemptInfo.canAttempt ? 'text-green-600' : 'text-red-600'}`}>
                                {attemptInfo.currentAttempts}/{attemptInfo.maxAttempts || '∞'}
                              </span>
                            </div>
                          )}
                        </div>

                        {/* Action Button */}
                        <div className="flex justify-center">
                          {isBlocked ? (
                            <button 
                              disabled 
                              className="w-full px-4 py-2 bg-gray-100 text-gray-400 rounded-lg font-medium cursor-not-allowed"
                            >
                              {status.label}
                            </button>
                          ) : (
                            <Link 
                              to={`${getBasePath(location, user)}/quizzes/${quiz.id}/attempt`}
                              className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 transition-colors duration-200"
                            >
                              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                              Start Quiz
                            </Link>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <div className="flex items-center justify-between">
                  <button 
                    disabled={page === 1} 
                    onClick={() => setPage(p => Math.max(p - 1, 1))} 
                    className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    Previous
                  </button>
                  
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-700">
                      Page <span className="font-medium">{page}</span> of <span className="font-medium">{totalPages}</span>
                    </span>
                  </div>
                  
                  <button 
                    disabled={page === totalPages} 
                    onClick={() => setPage(p => Math.min(p + 1, totalPages))} 
                    className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                  >
                    Next
                    <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default QuizzesPage;
